<script setup lang="ts">
import {computed} from "vue";

const props = defineProps<{ totalPages: number, pageRange?: number }>()
const pageNumber = defineModel<number>('page', {required: true})

const pageRange = computed(() => props.pageRange || 2)
const lastPageNumber = computed(() => props.totalPages - 1)

const minPageNumber = computed(() => Math.max(0, pageNumber.value - pageRange.value))
const maxPageNumber = computed(() => Math.min(lastPageNumber.value, pageNumber.value + pageRange.value))
const pageNumberItems = computed(() => {
  const items: number[] = []
  for (let i = minPageNumber.value; i <= maxPageNumber.value; i++) {
    items.push(i)
  }
  return items
})

const navFirstPage = () => pageNumber.value = 0
const navLastPage = () => pageNumber.value = lastPageNumber.value
const navPrevPage = () => pageNumber.value = Math.max(0, pageNumber.value - 1)
const navNextPage = () => pageNumber.value = Math.min(lastPageNumber.value, pageNumber.value + 1)
</script>

<template>
  <nav aria-label="Page Navigation">
    <ul class="list-inline g-mb-0">
      <li v-show="pageNumber > 0" class="list-inline-item">
        <a :href="`#${pageNumber}`"
           :key="pageNumber"
           class="u-pagination-v1__item u-pagination-v1-2 g-pa-7-16"
           aria-label="Prev"
           @click.prevent="navPrevPage"
        >
            <span aria-hidden="true">
              <i class="fa fa-angle-left"></i>
            </span>
          <span class="sr-only">Prev</span>
        </a>
      </li>

      <template v-if="minPageNumber > 0">
        <li class="list-inline-item g-hidden-sm-down">
          <a
              v-text="1"
              href="#1"
              class="u-pagination-v1__item u-pagination-v1-2 g-pa-7-14"
              :class="{'u-pagination-v1-2--active': pageNumber === 0}"
              @click.prevent="navFirstPage"
          />
        </li>
        <li v-if="minPageNumber > 1" class="list-inline-item g-hidden-sm-down">
          <a class="g-pa-7-14">...</a>
        </li>
      </template>

      <li v-for="item in pageNumberItems" :key="item" class="list-inline-item g-hidden-sm-down">
        <a
            v-text="item + 1"
            :href="`#${item + 1}`"
            class="u-pagination-v1__item u-pagination-v1-2 g-pa-7-14"
            :class="{'u-pagination-v1-2--active': pageNumber === item}"
            @click.prevent="pageNumber = item"
        />
      </li>

      <template v-if="maxPageNumber < lastPageNumber">
        <li v-if="maxPageNumber < (lastPageNumber - 1)" class="list-inline-item g-hidden-sm-down">
          <a class="g-pa-7-14">...</a>
        </li>
        <li class="list-inline-item g-hidden-sm-down">
          <a
              v-text="lastPageNumber + 1"
              :href="`#${lastPageNumber + 1}`"
              class="u-pagination-v1__item u-pagination-v1-2 g-pa-7-14"
              :class="{'u-pagination-v1-2--active': pageNumber === lastPageNumber}"
              @click.prevent="navLastPage"
          />
        </li>
      </template>

      <li v-show="pageNumber < lastPageNumber" class="list-inline-item">
        <a :href="`#${pageNumber + 2}`"
           :key="pageNumber"
           class="u-pagination-v1__item u-pagination-v1-2 g-pa-7-16"
           aria-label="Next"
           @click.prevent="navNextPage"
        >
            <span aria-hidden="true">
                <i class="fa fa-angle-right"></i>
            </span>
          <span class="sr-only">Next</span>
        </a>
      </li>
    </ul>
  </nav>
</template>
